import { AnimatePresence, motion } from "motion/react";
import type { FunctionComponent } from "react";
import { FiArrowLeft, FiSearch, FiX } from "react-icons/fi";
import { HiOutlineMenu } from "react-icons/hi";
import {
  type HeaderViewProps,
  useHeaderPresenter,
} from "@/app/layouts/headerPresenter";
import { SkipToMainContentLink } from "@/app/layouts/layoutAccessibility";
import icon_aimoge from "@/assets/img/icon_aimoge.webp";
import icon_aimoge_dark1 from "@/assets/img/icon_aimoge_dark1.webp";
import icon_blumoge from "@/assets/img/icon_blumoge.webp";
import { SearchBar } from "@/features/catalog/components";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";

export const Header: FunctionComponent = () => {
  const { node, ...props } = useHeaderPresenter();
  return (
    <ComponentScope node={node}>
      <HeaderView {...props} />
    </ComponentScope>
  );
};

export const HeaderView: FunctionComponent<HeaderViewProps> = ({
  displayName,
  mascot,
  isThreadView,
  isSearchAvailable,
  isSearchOpen,
  isHeaderVisible,
  onEvent,
}: HeaderViewProps) => {
  const handleNavigation = (): void => {
    onEvent({ type: "header.navigation-requested" });
  };
  const handleSearchToggle = (): void => {
    onEvent({ type: "header.search-toggle" });
  };

  return (
    <>
      <SkipToMainContentLink className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground" />
      <header
        className={`sticky top-0 z-20 pt-[env(safe-area-inset-top)] bg-card border-b border-border transition-transform duration-300 ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <section
          aria-label={`${displayName}のヘッダー操作`}
          className={`flex items-center justify-between px-4 py-0 ${
            isSearchAvailable
              ? ""
              : "after:block after:h-11 after:w-12 after:content-['']"
          }`}
        >
          <button
            type="button"
            onClick={handleNavigation}
            className="flex h-11 w-12 items-center justify-center rounded-full transition-colors hover:bg-muted"
            aria-label={isThreadView ? "戻る" : "メニュー"}
          >
            {isThreadView ? (
              <FiArrowLeft aria-hidden="true" className="w-6 h-6" />
            ) : (
              <HiOutlineMenu aria-hidden="true" className="w-6 h-6" />
            )}
          </button>

          <section
            aria-label={`${displayName}の案内`}
            className="flex flex-col items-center"
          >
            <strong className="flex items-center text-lg font-bold text-foreground">
              <img
                src={mascot}
                alt=""
                aria-hidden="true"
                className="mr-1 h-8 w-8 object-contain"
              />
              {displayName}
            </strong>
            <nav
              aria-label="関連サービス"
              className="flex max-w-full flex-wrap items-center justify-center gap-2 pb-1 pt-1"
            >
              <a
                href="https://vote.nijiurachan.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-11 shrink-0 items-center gap-1 px-1 text-sm text-secondary hover:text-primary transition-colors"
              >
                <img src={icon_aimoge} alt="" className="h-6 w-6" />
                投票所
              </a>
              <a
                href="https://aimoge.fanbox.cc/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-11 shrink-0 items-center gap-1 px-1 text-sm text-secondary hover:text-primary transition-colors"
              >
                <img src={icon_aimoge_dark1} alt="" className="h-6 w-6" />
                FANBOX
              </a>
              <a
                href="https://wiki.nijiurachan.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-11 shrink-0 items-center gap-1 px-1 text-sm text-secondary hover:text-primary transition-colors"
              >
                <img src={icon_blumoge} alt="" className="h-6 w-6" />
                Wiki
              </a>
            </nav>
          </section>

          {isSearchAvailable && (
            <>
              {/* デスクトップ: 検索バーを直接表示 */}
              <SearchBar className="hidden md:block" />
              {/* モバイル: 虫眼鏡アイコンを表示 */}
              <button
                type="button"
                onClick={handleSearchToggle}
                className="flex h-11 w-12 items-center justify-center rounded-full transition-colors hover:bg-muted md:hidden"
                aria-label={isSearchOpen ? "検索を閉じる" : "検索"}
              >
                {isSearchOpen ? (
                  <FiX aria-hidden="true" className="w-6 h-6" />
                ) : (
                  <FiSearch aria-hidden="true" className="w-6 h-6" />
                )}
              </button>
            </>
          )}
        </section>

        {/* モバイル: 検索バーを下に表示 */}
        {isSearchAvailable && (
          <AnimatePresence>
            {isSearchOpen && (
              <motion.div
                data-testid="mobile-header-search"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="md:hidden overflow-hidden"
              >
                <SearchBar className="flex justify-center px-4 py-3" />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </header>
    </>
  );
};
